import DeleteIcon from "@mui/icons-material/Delete";
import AlertIcon from "@mui/icons-material/ErrorOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Stack, Tooltip, Typography, useTheme } from "@mui/material";
import { groupBy, isEmpty } from "lodash";
import { Fragment, useMemo, useState } from "react";
import { UseFormReturn } from "react-hook-form";

import { Box } from "@/Components/v2/atoms/Box";
import { IconButton } from "@/Components/v2/atoms/Button";
import { useDefaultNodeNameInfo } from "@/hooks/shared/useDefaultNodeName";
import { FormatCurrencyFunction } from "@/hooks/useConfig/types";
import { ALL_CHANNELS_KEY } from "@/pages/scenario-builder/v3/api/constants";

import { HistoricalSpendDTO } from "../../api/getHistoricalSpend";
import {
  StyledAccordion,
  StyledAccordionDetails,
  StyledAccordionSummary,
  StyledTableHeaderRow,
} from "../../components/StyledComponents";
import { bcpLayerKey, BCPNode } from "../../utils/bcp";
import { getBusinessUnitHierarchy } from "../../utils/formatBusinessUnitHeirachy";
import { SpendInputFormData, useScenarioForms } from "../scenario-form/ScenarioFormProvider";
import { Constraint, SpendInput } from "../scenario-form/types";
import { HistoricalSpends } from "../scenario-form/utils/logic";
import { AddForecastConstraintRow } from "./AddConstraintRows";
import { ForecastConstraintRow } from "./ConstraintRows";
import { confirmDeleteBusinessUnit } from "./DeleteBusinessUnitConfirmationModal";
import { getHelperText } from "./utils/helperText";
import { channelsByBCPKey } from "./utils/logic";

const ForecastSpendInputTable: React.FC<{
  historicalSpends: HistoricalSpends;
  form: UseFormReturn<SpendInputFormData>;
  disabled?: boolean;
  currencySymbol: string;
  formatCurrency: FormatCurrencyFunction;
  isConstraintsFromApiEnabled: boolean;
  historicalSpendsDTO: HistoricalSpendDTO[];
}> = ({
  historicalSpends,
  form,
  disabled = false,
  currencySymbol,
  formatCurrency,
  isConstraintsFromApiEnabled,
  historicalSpendsDTO,
}) => {
  const theme = useTheme();
  const [collapsedPanels, setCollapsedPanels] = useState<string[]>([]);

  const values = form.watch();
  const {
    formState: { errors },
  } = form;

  const constraintsByBCPKey = useMemo(
    () => groupBy(values.spendInputs, (constraint) => bcpLayerKey(constraint)),
    [values.spendInputs]
  );

  const channelsByBCP = channelsByBCPKey(historicalSpendsDTO);

  const {
    handleSpendInputRowChange,
    handleRemoveSpendInputBusinessUnit,
    distinctBCP,
    spendConstraintsDTO,
  } = useScenarioForms();

  const { mapBcpName } = useDefaultNodeNameInfo();

  // Event Handlers
  const handleAccordionChange =
    (unitId: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      if ((event.target as Element).closest("[data-delete-button]")) return;
      setCollapsedPanels((prev) =>
        isExpanded ? prev.filter((id) => id !== unitId) : [...prev, unitId]
      );
    };

  const handleDeleteConfirm = async (bcp: BCPNode) => {
    const confirmed = await confirmDeleteBusinessUnit({ theme });
    if (confirmed) {
      handleRemoveSpendInputBusinessUnit(bcp);
    }
  };

  const removeConstraint = (index: number, constraint: SpendInput) => {
    const bcpConstraints = values.spendInputs.filter(
      (input: SpendInput) =>
        input.brand === constraint.brand &&
        input.category === constraint.category &&
        input.product === constraint.product
    );

    // If this was the only constraint for the BCP, set the channel to empty instead so we keep the Business Unit table
    if (bcpConstraints.length === 1) {
      handleSpendInputRowChange({
        type: "update",
        index,
        value: { ...constraint, channel: "", spend: 0 },
      });
      return;
    }

    handleSpendInputRowChange({
      type: "remove",
      index,
    });
  };

  const addConstraint = (constraint: SpendInput) => {
    handleSpendInputRowChange({
      type: "add",
      value: constraint,
    });
  };

  return (
    <>
      <Box>
        <Fragment>
          {Object.entries(constraintsByBCPKey).map(([bcpKey, constraints], index) => {
            const isExpanded = !collapsedPanels.includes(bcpKey);

            const selectedChannels = constraints.map((constraint) => constraint.channel);
            const isAllChannelsSelected = selectedChannels.includes(ALL_CHANNELS_KEY);
            const isNotAllChannelsSelected = selectedChannels.some(
              (channel) => channel && channel !== ALL_CHANNELS_KEY
            );

            const bcpChannels = channelsByBCP?.[bcpKey] ?? [];

            const availableChannels = [
              {
                key: ALL_CHANNELS_KEY,
                label: "All Channels",
                disabled: isNotAllChannelsSelected || isAllChannelsSelected,
              },
              ...bcpChannels.map((channel) => ({
                key: channel.key,
                label: channel.label,
                disabled: isAllChannelsSelected || selectedChannels.includes(channel.key),
              })),
            ];

            const bcp = distinctBCP?.get(bcpKey);

            if (!bcp) {
              console.error("BCP not found", bcpKey);
              return null;
            }

            const hasError = !isEmpty(errors.spendInputs?.[index]);

            return (
              <StyledAccordion
                key={bcpKey}
                expanded={isExpanded}
                onChange={handleAccordionChange(bcpKey)}
              >
                <StyledAccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    flexDirection: "row-reverse",
                    "& .MuiAccordionSummary-content": {
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      margin: 0,
                    },
                    "& .MuiAccordionSummary-expandIconWrapper": {
                      marginRight: 1,
                    },
                    ...(hasError &&
                      !isExpanded && {
                        borderColor: "error.main",
                        borderWidth: 1,
                        borderStyle: "solid",
                        color: "black",
                        backgroundColor: "error.light",
                      }),
                  }}
                >
                  <Stack sx={{ flex: 1 }} direction="row">
                    <Typography variant="subtitle1" sx={{ fontWeight: 400, fontSize: "1rem" }}>
                      {getBusinessUnitHierarchy(bcp, mapBcpName)}
                    </Typography>
                    {hasError && !isExpanded && (
                      <Tooltip
                        title="One or more of these constraints are invalid"
                        placement="top"
                        arrow
                      >
                        <AlertIcon sx={{ color: "error.main", ml: 1, cursor: "pointer" }} />
                      </Tooltip>
                    )}
                  </Stack>
                  {!disabled && (
                    <IconButton
                      size="small"
                      data-delete-button
                      onClick={(_event) => handleDeleteConfirm(bcp)}
                      sx={{
                        color: "text.secondary",
                        "&:hover": {
                          color: "error.main",
                          backgroundColor: "error.light",
                        },
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </StyledAccordionSummary>

                <StyledAccordionDetails>
                  <StyledTableHeaderRow sx={{ py: 0.5 }}>
                    <Box flex="1">
                      <Typography variant="subtitle2" fontWeight={600} color="text.primary">
                        Media Channel
                      </Typography>
                    </Box>
                    <Box flex="1">
                      <Typography variant="subtitle2" fontWeight={600} color="text.primary">
                        Spend
                      </Typography>
                    </Box>
                    <Box width="48px">
                      <span />
                    </Box>
                  </StyledTableHeaderRow>

                  {/* Constraint Rows */}
                  {constraints.map((constraint, constraintIndex) => {
                    const globalIndex = values.spendInputs.findIndex(
                      (c: SpendInput) =>
                        c.brand === constraint.brand &&
                        c.category === constraint.category &&
                        c.product === constraint.product &&
                        c.channel === constraint.channel
                    );

                    return (
                      <ForecastConstraintRow
                        key={`${constraint.brand}-${constraint.category}-${constraint.product}-${constraint.channel}-${constraintIndex}`}
                        form={form}
                        constraint={constraint}
                        globalIndex={globalIndex}
                        availableChannels={availableChannels}
                        disabled={disabled}
                        helperText={getHelperText(
                          bcpKey,
                          constraint,
                          historicalSpends,
                          spendConstraintsDTO,
                          formatCurrency,
                          isConstraintsFromApiEnabled
                        )}
                        onRemove={() => removeConstraint(globalIndex, constraint)}
                        currencySymbol={currencySymbol}
                      />
                    );
                  })}

                  <AddForecastConstraintRow
                    disabled={disabled}
                    availableChannels={availableChannels ?? []}
                    onAddConstraint={(channel: string) => {
                      // Validate channel is not empty
                      if (!channel || channel.trim() === "") {
                        return;
                      }

                      const isDuplicate = values.spendInputs.some(
                        (constraint: Constraint) =>
                          constraint.channel === channel &&
                          constraint.brand === bcp.brand &&
                          constraint.category === bcp.category &&
                          constraint.product === bcp.product
                      );

                      if (!isDuplicate) {
                        addConstraint({
                          channel,
                          brand: bcp.brand,
                          category: bcp.category,
                          product: bcp.product,
                          spend: null!, // Set empty instead of zero so we can see placeholder value
                        });
                      }
                    }}
                  />
                </StyledAccordionDetails>
              </StyledAccordion>
            );
          })}
        </Fragment>
      </Box>
    </>
  );
};

export default ForecastSpendInputTable;
